import React, { useEffect, useState, useMemo } from 'react';
import styled from 'styled-components';
import { getHarvests, deleteHarvest } from '../../../../../../services/apiaryService';
import Swal from 'sweetalert2';
import close from '../../../assets/images/close.png';
import * as Styles from '../../../Farmers/components/FarmerForm/style';
import { Row, Col, Button, Form } from 'react-bootstrap';
import LoadingOverlay from 'react-loading-overlay';
import BounceLoader from 'react-spinners/BounceLoader';
import BootstrapTable from 'react-bootstrap-table-next';
import paginationFactory from 'react-bootstrap-table2-paginator';
import { Select, DatePicker, Empty, Tag } from 'antd';
import moment from 'moment';
import 'react-bootstrap-table-next/dist/react-bootstrap-table2.min.css';
import 'react-bootstrap-table2-paginator/dist/react-bootstrap-table2-paginator.min.css';

const { Option } = Select;

// --- Styled Components ---

const MainContainer = styled.div`
    padding: 20px 0;
`;

const HeaderSection = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;
`;

const SummaryCards = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 20px;
    margin-bottom: 30px;
`;

const StatCard = styled.div`
    background: linear-gradient(135deg, ${props => props.color || '#ff8701'} 0%, ${props => props.color2 || '#ffbc6d'} 100%);
    padding: 20px;
    border-radius: 16px;
    color: white;
    box-shadow: 0 8px 16px rgba(0,0,0,0.08);
    transition: transform 0.3s ease;

    &:hover {
        transform: translateY(-5px);
    }

    .label {
        font-size: 13px;
        font-weight: 600;
        opacity: 0.9;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        margin-bottom: 8px;
    }

    .value {
        font-size: 28px;
        font-weight: 800;
        margin: 0;
    }

    .sub-value {
        font-size: 12px;
        margin-top: 5px;
        opacity: 0.8;
    }
`;

const FilterCard = styled.div`
    background: #fdfdfd;
    padding: 24px;
    border-radius: 12px;
    margin-bottom: 30px;
    border: 1px solid #f0f0f0;
    box-shadow: 0 2px 8px rgba(0,0,0,0.02);
`;

const TableWrapper = styled.div`
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 4px 12px rgba(0,0,0,0.03);
    overflow: hidden;
    border: 1px solid #eaeaea;

    .modern-table thead th {
        background: #fafafa;
        color: #636e72;
        font-weight: 700;
        font-size: 12px;
        padding: 16px;
        text-transform: uppercase;
        border-bottom: 2px solid #ffcc00 !important;
    }

    .modern-table tbody td {
        padding: 16px;
        vertical-align: middle;
        font-size: 14px;
        border-top: 1px solid #f8f8f8;
    }

    .modern-table tbody tr:hover {
        background-color: #fffdf5 !important;
    }
`;

const GradeBadge = styled.span`
    padding: 4px 12px;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    background: ${props => {
        if (props.grade?.startsWith('A')) return '#e6fffa';
        if (props.grade?.startsWith('B')) return '#fffbe6';
        return '#f9f9f9';
    }};
    color: ${props => {
        if (props.grade?.startsWith('A')) return '#00b894';
        if (props.grade?.startsWith('B')) return '#d4a017';
        return '#636e72';
    }};
    border: 1px solid ${props => {
        if (props.grade?.startsWith('A')) return '#b2f2bb';
        if (props.grade?.startsWith('B')) return '#ffe58f';
        return '#d9d9d9';
    }};
`;

const SensoryTag = styled(Tag)`
    border-radius: 4px;
    font-size: 11px;
    margin-bottom: 4px;
    font-weight: 600;
`;

// --- Detail Modal ---

const ModalSection = styled.div`
    margin-bottom: 30px;

    h5 {
        font-size: 15px;
        font-weight: 700;
        color: #2d3436;
        border-left: 4px solid #ffcc00;
        padding-left: 12px;
        margin-bottom: 20px;
        line-height: 1.2;
    }
`;

const MetricGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
    gap: 15px;
`;

const MetricItem = styled.div`
    background: #f8f9fa;
    padding: 12px;
    border-radius: 10px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    border: 1px solid #f1f2f6;

    .m-label {
        font-size: 11px;
        color: #b2bec3;
        font-weight: 700;
        text-transform: uppercase;
        margin-bottom: 5px;
    }
    .m-value {
        font-size: 18px;
        font-weight: 700;
        color: #2d3436;
    }
    .m-unit {
        font-size: 11px;
        color: #636e72;
        margin-top: 2px;
    }
`;

const PhotoGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
    gap: 12px;
    margin-top: 10px;
`;

const PhotoItem = styled.div`
    aspect-ratio: 1;
    border-radius: 8px;
    overflow: hidden;
    cursor: pointer;
    border: 1px solid #eee;

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
`;

const HarvestDetailModal = ({ data, onClose }) => {
    if (!data) return null;

    let flowerName = '';
    if (data.selected_flower) {
        if (typeof data.selected_flower === 'object') {
            flowerName = data.selected_flower.localName || data.selected_flower.plant || '';
        } else if (typeof data.selected_flower === 'string') {
            flowerName = data.selected_flower;
        }
    }

    const sections = [
        {
            title: 'Harvest Overview',
            metrics: [
                { label: 'Weight', value: data.total_honey_kg, unit: 'KG' },
                { label: 'Hives count', value: data.hives_count, unit: 'Hives' },
                { label: 'Super Frames', value: data.super_frames, unit: 'Frames' },
                { label: 'Brood Frames', value: data.brood_frames, unit: 'Frames' },
                { label: 'Sealed', value: data.frames_wax_sealed, unit: '' },
                { label: 'Flower Type', value: data.flowering_type, unit: '' },
                { label: 'Flower', value: flowerName || '-', unit: '' }
            ]
        },
        {
            title: 'Honey Quality & Environment',
            metrics: [
                { label: 'Brix Content', value: data.brix_value, unit: '%' },
                { label: 'Moisture', value: data.moisture_content, unit: '%' },
                { label: 'Temperature', value: data.harvest_temperature, unit: '°C' },
                { label: 'Humidity', value: data.harvest_humidity, unit: '%' },
                { label: 'Grade', value: data.grade, unit: '' }
            ]
        }
    ];

    const sensoryProfile = [
        { label: 'Aroma', values: data.aroma_profile, color: 'orange' },
        { label: 'Texture', values: data.texture, color: 'gold' },
        { label: 'Taste', values: data.taste, color: 'volcano' },
        { label: 'Colour', values: [data.colour], color: 'brown' }
    ];

    const extractionProtocol = data.extraction_protocol || {};

    return (
        <Styles.ModalWrapper style={{ zIndex: 1060 }}>
            <Styles.ModalContent2 style={{ maxWidth: '850px', width: '95%' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '24px 24px 0 24px' }}>
                    <div>
                        <h3 style={{ margin: 0, color: '#f39c12', fontSize: '22px' }}>Harvest Details</h3>
                        <p style={{ margin: 0, color: '#b2bec3', fontSize: '13px' }}>{data.harvest_id} | {moment(data.harvested_on).format('DD MMM YYYY')}</p>
                    </div>
                    <img src={close} alt="Close" onClick={onClose} style={{ width: 18, cursor: 'pointer', opacity: 0.5 }} />
                </div>

                <div style={{ padding: '24px', maxHeight: '80vh', overflowY: 'auto' }}>
                    <SummaryCards style={{ marginBottom: '24px' }}>
                         <StatCard color="#f39c12" color2="#ffcc00">
                            <div className="label">Grade / Quality</div>
                            <div className="value">{data.grade || 'N/A'}</div>
                            <div className="sub-value">{data.crystallization || 'No crystallization'}</div>
                        </StatCard>
                        <StatCard color="#e67e22" color2="#f39c12">
                            <div className="label">Total Quantity</div>
                            <div className="value">{data.total_honey_kg} <span style={{ fontSize: '16px' }}>kg</span></div>
                            <div className="sub-value">{data.hives_count} hives harvested from</div>
                        </StatCard>
                    </SummaryCards>

                    {sections.map((section, idx) => (
                        <ModalSection key={idx}>
                            <h5>{section.title}</h5>
                            <MetricGrid>
                                {section.metrics.map((m, i) => (
                                    <MetricItem key={i}>
                                        <div className="m-label">{m.label}</div>
                                        <div className="m-value">{m.value ?? 'N/A'}</div>
                                        <div className="m-unit">{m.unit}</div>
                                    </MetricItem>
                                ))}
                            </MetricGrid>
                        </ModalSection>
                    ))}

                    <Row>
                        <Col md={7}>
                            <ModalSection>
                                <h5>Colony Health Snapshot</h5>
                                <div style={{ background: '#f8f9fa', padding: '15px', borderRadius: '12px', border: '1px solid #eee' }}>
                                    {data.colony_health_snapshot && data.colony_health_snapshot.length > 0 ? (
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                            {data.colony_health_snapshot.map((tag, i) => (
                                                <Tag key={i} color="green" style={{ borderRadius: '4px', margin: 0 }}>{tag}</Tag>
                                            ))}
                                        </div>
                                    ) : <span style={{ fontSize: '12px', color: '#ccc' }}>No health data recorded</span>}
                                    <div style={{ marginTop: '15px', display: 'flex', gap: '20px' }}>
                                        <div>
                                            <div style={{ fontSize: '11px', color: '#b2bec3', fontWeight: 700, textTransform: 'uppercase' }}>Colony Strength</div>
                                            <div style={{ fontWeight: 600, fontSize: '13px' }}>{data.colony_strength || 'N/A'}</div>
                                        </div>
                                        <div>
                                            <div style={{ fontSize: '11px', color: '#b2bec3', fontWeight: 700, textTransform: 'uppercase' }}>Supers in Hive</div>
                                            <div style={{ fontWeight: 600, fontSize: '13px' }}>{data.supers_in_hive || 'N/A'}</div>
                                        </div>
                                    </div>
                                </div>
                            </ModalSection>
                        </Col>
                        <Col md={5}>
                             <ModalSection>
                                <h5>Extraction Protocol</h5>
                                <div style={{ background: '#fff', padding: '15px', borderRadius: '12px', border: '1px solid #eee', fontSize: '12px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                                        <span>Closed Space:</span>
                                        <strong>{extractionProtocol.closed_space ? 'YES' : 'NO'}</strong>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                                        <span>SS Extractor:</span>
                                        <strong>{extractionProtocol.ss_extractor ? 'YES' : 'NO'}</strong>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                                        <span>Gloves Worn:</span>
                                        <strong>{extractionProtocol.gloves_worn ? 'YES' : 'NO'}</strong>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <span>Time:</span>
                                        <strong>{extractionProtocol.start_time || 'N/A'} - {extractionProtocol.end_time || 'N/A'}</strong>
                                    </div>
                                </div>
                            </ModalSection>
                        </Col>
                    </Row>

                    <ModalSection>
                        <h5>Sensory Profile</h5>
                        <div style={{ background: '#fcfcfc', padding: '20px', borderRadius: '12px', border: '1px solid #f0f0f0' }}>
                            <Row>
                                {sensoryProfile.map((profile, i) => (
                                    <Col md={3} sm={6} key={i} className="mb-3">
                                        <div style={{ fontSize: '12px', fontWeight: 700, color: '#b2bec3', marginBottom: '8px', textTransform: 'uppercase' }}>{profile.label}</div>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                            {profile.values && profile.values.length > 0 ? (
                                                profile.values.map((val, j) => val && <SensoryTag key={j} color={profile.color}>{val.replace(/_/g, ' ')}</SensoryTag>)
                                            ) : (
                                                <span style={{ fontSize: '12px', color: '#ccc' }}>N/A</span>
                                            )}
                                        </div>
                                    </Col>
                                ))}
                            </Row>
                        </div>
                    </ModalSection>

                    {((data.harvest_photos && data.harvest_photos.length > 0) || (data.brix_photos && data.brix_photos.length > 0)) && (
                        <ModalSection>
                            <h5>Quality Inspection Photos</h5>
                            <Row>
                                <Col md={6}>
                                    <div style={{ fontSize: '12px', fontWeight: 600, color: '#2d3436', marginBottom: '10px' }}>Harvesting Site</div>
                                    {data.harvest_photos && data.harvest_photos.length > 0 ? (
                                        <PhotoGrid>
                                             {data.harvest_photos.map((p, i) => (
                                                <PhotoItem key={i} onClick={() => window.open(p, '_blank')}>
                                                    <img src={p} alt="Harvest" onError={(e) => { e.target.src = 'https://placehold.co/400x300?text=Image+Error'; }} />
                                                </PhotoItem>
                                             ))}
                                        </PhotoGrid>
                                    ) : <p style={{ fontSize: '12px', color: '#ccc' }}>No harvest site photos</p>}
                                </Col>
                                <Col md={6}>
                                    <div style={{ fontSize: '12px', fontWeight: 600, color: '#2d3436', marginBottom: '10px' }}>Refractometer / Brix Check</div>
                                    {data.brix_photos && data.brix_photos.length > 0 ? (
                                        <PhotoGrid>
                                             {data.brix_photos.map((p, i) => (
                                                <PhotoItem key={i} onClick={() => window.open(p, '_blank')}>
                                                    <img src={p} alt="Brix Check" onError={(e) => { e.target.src = 'https://placehold.co/400x300?text=Image+Error'; }} />
                                                </PhotoItem>
                                             ))}
                                        </PhotoGrid>
                                    ) : <p style={{ fontSize: '12px', color: '#ccc' }}>No Brix check photos</p>}
                                </Col>
                            </Row>
                        </ModalSection>
                    )}

                    <ModalSection className="mb-0">
                        <h5>Storage & Sign-off</h5>
                        <div style={{ background: '#fff9f0', border: '1px solid #ffe8d6', padding: '20px', borderRadius: '12px' }}>
                            <Row>
                                <Col md={4}>
                                    <p style={{ margin: '0 0 5px', fontSize: '12px', color: '#b2bec3' }}><strong>Container:</strong> <span style={{ color: '#2d3436' }}>{(data.volume_storage && data.volume_storage.storage_container) || 'N/A'}</span></p>
                                    <p style={{ margin: '0 0 5px', fontSize: '12px', color: '#b2bec3' }}><strong>Labelled:</strong> <span style={{ color: '#2d3436' }}>{(data.volume_storage && data.volume_storage.labelled) ? 'YES' : 'NO'}</span></p>
                                    <p style={{ margin: 0, fontSize: '12px', color: '#b2bec3' }}><strong>Strained:</strong> <span style={{ color: '#2d3436' }}>{(data.volume_storage && data.volume_storage.strained) ? 'YES' : 'NO'}</span></p>
                                </Col>
                                <Col md={8}>
                                    <p style={{ margin: '0 0 5px', fontSize: '12px', fontWeight: 700, color: '#ff8701' }}>Supervisor Remarks</p>
                                    <p style={{ margin: 0, fontSize: '14px', color: '#2d3436' }}>{data.supervisor_remarks || (data.sign_off && data.sign_off.supervisor_remarks) || 'No remarks provided.'}</p>
                                    {data.additional_remarks && (
                                        <p style={{ margin: '10px 0 0', fontSize: '12px', color: '#636e72', fontStyle: 'italic' }}>Note: {data.additional_remarks}</p>
                                    )}
                                </Col>
                            </Row>
                        </div>
                    </ModalSection>
                </div>
            </Styles.ModalContent2>
        </Styles.ModalWrapper>
    );
};

// --- Main Component ---

const NectarHarvests = ({ apiaryId }) => {
    const [harvests, setHarvests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedItem, setSelectedItem] = useState(null);
    const [page, setPage] = useState(1);
    const [sizePerPage, setSizePerPage] = useState(1000);

    // Filters
    const [filters, setFilters] = useState({
        bee_species: '',
        harvest_method: '',
        dateRange: null
    });

    const fetchHarvestsData = async () => {
        if (!apiaryId) return;
        setLoading(true);
        try {
            const apiFilters = {};
            if (filters.bee_species) apiFilters.bee_species = filters.bee_species;
            if (filters.harvest_method) apiFilters.harvest_method = filters.harvest_method;
            if (filters.dateRange && filters.dateRange[0] && filters.dateRange[1]) {
                apiFilters.date_from = filters.dateRange[0].format('YYYY-MM-DD');
                apiFilters.date_to = filters.dateRange[1].format('YYYY-MM-DD');
            }

            const data = await getHarvests(apiaryId, apiFilters);
            setHarvests(data || []);
        } catch (error) {
            console.error('Failed to load harvests:', error);
            setHarvests([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchHarvestsData();
    }, [apiaryId, filters.bee_species, filters.harvest_method, filters.dateRange]);

    const handleDeleteClick = async (harvestId) => {
        const result = await Swal.fire({
            title: 'Delete Harvest Log',
            text: 'Are you sure you want to permanently delete this harvest record? This action cannot be undone.',
            type: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d63031',
            cancelButtonColor: '#636e72',
            confirmButtonText: 'Yes, Delete',
            cancelButtonText: 'Cancel'
        });

        if (result.value) {
            Swal.fire({
                title: 'Deleting...',
                allowOutsideClick: false,
                onBeforeOpen: () => {
                    Swal.showLoading();
                }
            });

            try {
                await deleteHarvest(harvestId);
                await Swal.fire({
                    title: 'Deleted!',
                    text: 'Harvest log has been deleted successfully.',
                    type: 'success',
                    confirmButtonColor: '#ff8701'
                });
                // Remove from local state
                setHarvests(prev => prev.filter(h => h.harvest_id !== harvestId));
            } catch (error) {
                console.error("Error deleting harvest:", error);
                await Swal.fire({
                    title: 'Error',
                    text: error.response?.data?.error || 'Failed to delete harvest log. Please try again.',
                    type: 'error',
                    confirmButtonColor: '#ff8701'
                });
            }
        }
    };

    const summaryStats = useMemo(() => {
        const total = harvests.reduce((acc, h) => acc + (h.total_honey_kg || 0), 0);
        const avgBrix = harvests.length > 0 
            ? harvests.reduce((acc, h) => acc + (h.brix_value || 0), 0) / harvests.length 
            : 0;
        const totalHives = harvests.reduce((acc, h) => acc + (h.hives_count || 0), 0);
        
        return {
            total: total.toFixed(1),
            avgBrix: avgBrix.toFixed(1),
            totalHives,
            count: harvests.length
        };
    }, [harvests]);

    const paginatedHarvests = useMemo(() => {
        const start = (page - 1) * sizePerPage;
        return harvests.slice(start, start + sizePerPage);
    }, [harvests, page, sizePerPage]);

    const totalPages = Math.ceil(harvests.length / sizePerPage) || 1;

    const columns = [
        {
            dataField: 'harvested_on',
            text: 'Date',
            sort: true,
            formatter: (cell) => moment(cell).format('DD MMM YYYY'),
            style: { fontWeight: 'bold', color: '#2d3436' }
        },
        {
            dataField: 'total_honey_kg',
            text: 'Quantity',
            sort: true,
            formatter: (cell) => <span style={{ color: '#e67e22', fontWeight: 700 }}>{cell} kg</span>
        },
        {
            dataField: 'hives_count',
            text: 'Hives',
            sort: true,
            style: { textAlign: 'center' },
            headerStyle: { textAlign: 'center' }
        },
        {
            dataField: 'brix_value',
            text: 'Brix %',
            sort: true,
            formatter: (cell) => <span style={{ color: '#f39c12', fontWeight: 600 }}>{cell || '-'}%</span>,
            style: { textAlign: 'center' },
            headerStyle: { textAlign: 'center' }
        },
        {
            dataField: 'grade',
            text: 'Grade',
            formatter: (cell) => <GradeBadge grade={cell}>{cell || 'N/A'}</GradeBadge>,
            style: { textAlign: 'center' },
            headerStyle: { textAlign: 'center' }
        },
        {
            dataField: 'flowering_type',
            text: 'Type of Honey',
            sort: true,
            formatter: (cell, row) => {
                if (!cell) return '-';
                if (cell.toLowerCase() === 'monofloral' && row.selected_flower) {
                    let flowerName = '';
                    if (typeof row.selected_flower === 'object') {
                        flowerName = row.selected_flower.localName || row.selected_flower.plant || '';
                    } else if (typeof row.selected_flower === 'string') {
                        flowerName = row.selected_flower;
                    }
                    return flowerName ? `Monofloral (${flowerName})` : 'Monofloral';
                }
                if (cell.toLowerCase() === 'multifloral') {
                    return 'Multifloral';
                }
                return cell;
            },
            style: { fontWeight: '600' }
        },
        {
            dataField: 'bee_species',
            text: 'Bee Species',
            formatter: (cell) => <span style={{ fontSize: '12px', color: '#636e72' }}>{cell?.replace(/_/g, ' ')}</span>
        },
        {
            dataField: 'view',
            text: '',
            formatter: (_, row) => (
                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                    <Button 
                        variant="link" 
                        size="sm" 
                        onClick={() => setSelectedItem(row)}
                        style={{ color: '#f39c12', textDecoration: 'none', fontWeight: 700 }}
                    >
                        View Details
                    </Button>
                    <Button 
                        variant="link" 
                        size="sm" 
                        onClick={() => handleDeleteClick(row.harvest_id)}
                        style={{ color: '#ff7675', textDecoration: 'none', fontWeight: 700 }}
                    >
                        Delete
                    </Button>
                </div>
            ),
            headerStyle: { width: '180px' }
        }
    ];

    const resetFilters = () => {
        setFilters({ bee_species: '', harvest_method: '', dateRange: null });
    };

    return (
        <MainContainer>
            <HeaderSection>
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                    <h4 style={{ margin: 0, color: '#2d3436', fontWeight: 700 }}>Harvest Logs</h4>
                    <Tag color="orange" style={{ borderRadius: '12px', fontWeight: 700 }}>{harvests.length} Recorded</Tag>
                </div>
                <Button 
                    variant="outline-warning" 
                    size="sm" 
                    onClick={() => window.location.href = '/nector-harvests'}
                    style={{ fontWeight: 700, borderRadius: '8px', border: '2px solid #f39c12', color: '#f39c12' }}
                >
                    View Global Records →
                </Button>
            </HeaderSection>

            <SummaryCards>
                <StatCard color="#f39c12" color2="#ffcc00">
                    <div className="label">Total Honey Yield</div>
                    <div className="value">{summaryStats.total} <span style={{ fontSize: '16px' }}>kg</span></div>
                    <div className="sub-value">From {summaryStats.totalHives} total hive instances</div>
                </StatCard>
                <StatCard color="#e67e22" color2="#f39c12">
                    <div className="label">Average Quality</div>
                    <div className="value">{summaryStats.avgBrix}<span style={{ fontSize: '16px' }}>%</span></div>
                    <div className="sub-value">Average Brix content across logs</div>
                </StatCard>
                <StatCard color="#d35400" color2="#e67e22">
                    <div className="label">Entries</div>
                    <div className="value">{summaryStats.count}</div>
                    <div className="sub-value">Individual harvest events</div>
                </StatCard>
            </SummaryCards>

            <FilterCard>
                <Row className="align-items-end">
                    <Col md={3}>
                        <Form.Label style={{ fontSize: '12px', fontWeight: 700, color: '#b2bec3', textTransform: 'uppercase' }}>Bee Species</Form.Label>
                        <Select 
                            style={{ width: '100%', height: '38px' }} 
                            placeholder="All Species"
                            value={filters.bee_species || undefined}
                            onChange={v => setFilters({ ...filters, bee_species: v })}
                            allowClear
                        >
                            <Option value="Apis mellifera">Apis Mellifera</Option>
                            <Option value="Apis cerana">Apis Cerana</Option>
                        </Select>
                    </Col>
                    <Col md={3}>
                        <Form.Label style={{ fontSize: '12px', fontWeight: 700, color: '#b2bec3', textTransform: 'uppercase' }}>Method</Form.Label>
                        <Select 
                            style={{ width: '100%', height: '38px' }} 
                            placeholder="All Methods"
                            value={filters.harvest_method || undefined}
                            onChange={v => setFilters({ ...filters, harvest_method: v })}
                            allowClear
                        >
                            <Option value="sealed_super">Sealed Super</Option>
                            <Option value="unsealed_partial">Unsealed Partial</Option>
                            <Option value="wild_harvest">Wild Harvest</Option>
                            <Option value="stingless">Stingless</Option>
                        </Select>
                    </Col>
                    <Col md={4}>
                        <Form.Label style={{ fontSize: '12px', fontWeight: 700, color: '#b2bec3', textTransform: 'uppercase' }}>Date Range</Form.Label>
                        <DatePicker.RangePicker 
                            style={{ width: '100%', height: '38px' }} 
                            value={filters.dateRange}
                            onChange={dates => setFilters({ ...filters, dateRange: dates })}
                        />
                    </Col>
                    <Col md={2}>
                        <Button 
                            variant="outline-secondary" 
                            style={{ width: '100%', height: '38px', fontWeight: 600, border: '1px solid #d9d9d9', color: '#636e72' }}
                            onClick={resetFilters}
                        >
                            Reset
                        </Button>
                    </Col>
                </Row>
            </FilterCard>

            <LoadingOverlay
                active={loading}
                spinner={<BounceLoader color={'#f39c12'} />}
                text='Fetching production logs...'
                styles={{
                    overlay: (base) => ({
                        ...base,
                        background: 'rgba(255, 255, 255, 0.8)',
                        zIndex: 10
                    })
                }}
            >
                {harvests.length === 0 && !loading ? (
                    <Empty 
                        image={Empty.PRESENTED_IMAGE_SIMPLE} 
                        description="No harvest records found" 
                        style={{ padding: '60px 0', background: '#fff', borderRadius: '12px', border: '1px dashed #eee' }}
                    />
                ) : (
                    <>
                        <TableWrapper>
                            <BootstrapTable
                                keyField="harvest_id"
                                data={paginatedHarvests}
                                columns={columns}
                                hover
                                bordered={false}
                                className="modern-table"
                            />
                        </TableWrapper>

                        <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginTop: '20px', paddingTop: '10px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                                <span>
                                    <strong>
                                        Page {page} of {totalPages}
                                    </strong>
                                </span>
                                <select
                                    value={sizePerPage}
                                    onChange={e => {
                                        setSizePerPage(Number(e.target.value));
                                        setPage(1);
                                    }}
                                    style={{
                                        padding: '5px 10px',
                                        borderRadius: '4px',
                                        border: '1px solid #d9d9d9',
                                        cursor: 'pointer',
                                        fontSize: '14px',
                                    }}
                                >
                                    {[5, 10, 25, 50, 100, 500, 1000, 2000, 3000, 5000].map(pageSizeOption => (
                                        <option key={pageSizeOption} value={pageSizeOption}>
                                            Show {pageSizeOption}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center', gap: '20px' }}>
                                <button
                                    onClick={() => setPage(1)}
                                    disabled={page === 1}
                                    style={{ background: '#ff8701', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', opacity: page === 1 ? 0.5 : 1 }}
                                >
                                    {'<<'}
                                </button>
                                <button
                                    onClick={() => setPage(p => Math.max(1, p - 1))}
                                    disabled={page === 1}
                                    style={{ background: '#ff8701', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', opacity: page === 1 ? 0.5 : 1 }}
                                >
                                    Previous
                                </button>
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <input
                                        type="number"
                                        value={page}
                                        min={1}
                                        max={totalPages}
                                        onChange={e => {
                                            const val = Number(e.target.value) || 1;
                                            setPage(Math.min(totalPages, Math.max(1, val)));
                                        }}
                                        style={{ width: '45px', textAlign: 'center', padding: '4px', border: '1px solid #d9d9d9', borderRadius: '4px' }}
                                    />
                                </div>
                                <button
                                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                    disabled={page >= totalPages}
                                    style={{ background: '#ff8701', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', opacity: page >= totalPages ? 0.5 : 1 }}
                                >
                                    Next
                                </button>
                                <button
                                    onClick={() => setPage(totalPages)}
                                    disabled={page >= totalPages}
                                    style={{ background: '#ff8701', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', opacity: page >= totalPages ? 0.5 : 1 }}
                                >
                                    {'>>'}
                                </button>
                            </div>
                        </div>
                    </>
                )}
            </LoadingOverlay>

            {selectedItem && (
                <HarvestDetailModal 
                    data={selectedItem} 
                    onClose={() => setSelectedItem(null)} 
                />
            )}
        </MainContainer>
    );
};

export default NectarHarvests;
